import { useCallback, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react'

const CARD_SELECTOR = '[data-channel-card]'
const DRAG_THRESHOLD_PX = 4

export interface ChannelCardDragHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
}

interface DragSession {
  pointerId: number
  draggedId: string
  ids: string[]
  originX: number
  originY: number
  active: boolean
}

function moveId(ids: readonly string[], draggedId: string, targetId: string): string[] {
  const from = ids.indexOf(draggedId)
  const to = ids.indexOf(targetId)
  if (from < 0 || to < 0 || from === to) return ids.slice()
  const next = ids.slice()
  next.splice(from, 1)
  next.splice(to, 0, draggedId)
  return next
}

function cardIdFromPoint(root: HTMLElement, x: number, y: number, draggedId: string): string | null {
  const stack = document.elementsFromPoint(x, y)
  for (const node of stack) {
    if (!(node instanceof Element)) continue
    const card = node.closest(CARD_SELECTOR)
    if (!(card instanceof HTMLElement) || !root.contains(card)) continue
    const id = card.dataset.channelId
    if (id && id !== draggedId) return id
  }
  return null
}

function paint(root: HTMLElement, ids: readonly string[], draggedId: string) {
  root.querySelectorAll<HTMLElement>(CARD_SELECTOR).forEach((card) => {
    const id = card.dataset.channelId
    if (!id) return
    const index = ids.indexOf(id)
    if (index >= 0) card.style.order = String(index)
    if (id === draggedId) card.dataset.dragging = 'true'
    else delete card.dataset.dragging
  })
}

function clearPaint(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>(CARD_SELECTOR).forEach((card) => {
    card.style.order = ''
    delete card.dataset.dragging
  })
}

export function useChannelCardDrag(
  orderedIds: readonly string[],
  onCommit: (ids: readonly string[]) => void,
) {
  const gridRef = useRef<HTMLDivElement>(null)
  const sessionRef = useRef<DragSession | null>(null)
  const grabbingRef = useRef(false)
  const orderedIdsRef = useRef(orderedIds)
  orderedIdsRef.current = orderedIds
  const orderKey = orderedIds.join('\0')

  const setGrabbing = useCallback((active: boolean) => {
    if (grabbingRef.current === active) return
    grabbingRef.current = active
    document.body.style.cursor = active ? 'grabbing' : ''
  }, [])

  useLayoutEffect(() => {
    const root = gridRef.current
    if (!root || sessionRef.current) return
    clearPaint(root)
    setGrabbing(false)
  }, [orderKey, setGrabbing])

  useLayoutEffect(() => () => {
    sessionRef.current = null
    setGrabbing(false)
  }, [setGrabbing])

  const endSession = useCallback((event: ReactPointerEvent<HTMLElement>, commit: boolean) => {
    const session = sessionRef.current
    if (!session || session.pointerId !== event.pointerId) return
    sessionRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const changed = session.active && session.ids.some((id, index) => orderedIdsRef.current[index] !== id)
    if (commit && changed) {
      onCommit(session.ids)
      return
    }
    const root = gridRef.current
    if (root) clearPaint(root)
    setGrabbing(false)
  }, [onCommit, setGrabbing])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    const card = event.currentTarget.closest(CARD_SELECTOR)
    if (!(card instanceof HTMLElement)) return
    const draggedId = card.dataset.channelId
    if (!draggedId || !gridRef.current) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    sessionRef.current = {
      pointerId: event.pointerId,
      draggedId,
      ids: orderedIdsRef.current.slice(),
      originX: event.clientX,
      originY: event.clientY,
      active: false,
    }
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const session = sessionRef.current
    const root = gridRef.current
    if (!session || !root || session.pointerId !== event.pointerId) return
    if (!session.active) {
      const dx = event.clientX - session.originX
      const dy = event.clientY - session.originY
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return
      session.active = true
      setGrabbing(true)
      paint(root, session.ids, session.draggedId)
    }
    const targetId = cardIdFromPoint(root, event.clientX, event.clientY, session.draggedId)
    if (!targetId) return
    const next = moveId(session.ids, session.draggedId, targetId)
    if (next.every((id, index) => id === session.ids[index])) return
    session.ids = next
    paint(root, next, session.draggedId)
  }, [setGrabbing])

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation()
    endSession(event, true)
  }, [endSession])

  const onPointerCancel = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    endSession(event, false)
  }, [endSession])

  const dragHandleProps: ChannelCardDragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  }

  return { gridRef, dragHandleProps }
}
